import { createSlice } from '@reduxjs/toolkit';

type LastLoadedAsset = {
  assetClassId: string;
  assetId: string;
  tp2AssetId?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>;
};

type IAssetState = {
  lastLoadedAssetByTenant: Record<string, LastLoadedAsset>;
  /**
   * The id of the most recently accessed asset, keyed by tenant pid. Drives the
   * "recently accessed tree" map highlight. Kept separate from
   * lastLoadedAssetByTenant (which powers the Load Last template feature) so the
   * two concerns can evolve independently.
   */
  lastAccessedAssetIdByTenant: Record<string, string>;
  hasPendingPhotoUploads: boolean;
  hasActiveCompressions: boolean;
  cancelUploadsRequested: boolean;
};

const initialState: IAssetState = {
  lastLoadedAssetByTenant: {},
  lastAccessedAssetIdByTenant: {},
  hasPendingPhotoUploads: false,
  hasActiveCompressions: false,
  cancelUploadsRequested: false,
};

const slice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    saveLastLoadedAsset(
      state: IAssetState,
      action: { payload: { tenant: string; asset: LastLoadedAsset } }
    ) {
      return {
        ...state,
        lastLoadedAssetByTenant: {
          ...state.lastLoadedAssetByTenant,
          [action.payload.tenant]: action.payload.asset,
        },
      };
    },
    setLastAccessedAsset(
      state: IAssetState,
      action: { payload: { tenant: string; assetId: string } }
    ) {
      return {
        ...state,
        lastAccessedAssetIdByTenant: {
          ...state.lastAccessedAssetIdByTenant,
          [action.payload.tenant]: action.payload.assetId,
        },
      };
    },
    clearLastAccessedAsset(state: IAssetState, action: { payload: string }) {
      const lastAccessedAssetIdByTenant = { ...state.lastAccessedAssetIdByTenant };
      delete lastAccessedAssetIdByTenant[action.payload];

      return {
        ...state,
        lastAccessedAssetIdByTenant,
      };
    },
    clearLastLoadedAsset(state: IAssetState, action: { payload: string }) {
      const newState = { ...state };
      const newLastLoadedAssetByTenant = { ...state.lastLoadedAssetByTenant };
      delete newLastLoadedAssetByTenant[action.payload];

      return {
        ...newState,
        lastLoadedAssetByTenant: newLastLoadedAssetByTenant,
      };
    },
    setHasPendingPhotoUploads(state: IAssetState, action: { payload: boolean }) {
      state.hasPendingPhotoUploads = action.payload;
    },
    setHasActiveCompressions(state: IAssetState, action: { payload: boolean }) {
      state.hasActiveCompressions = action.payload;
    },
    setCancelUploadsRequested(state: IAssetState, action: { payload: boolean }) {
      state.cancelUploadsRequested = action.payload;
    },
  },
});

const { reducer } = slice;
const {
  saveLastLoadedAsset,
  clearLastLoadedAsset,
  setLastAccessedAsset,
  clearLastAccessedAsset,
  setHasPendingPhotoUploads,
  setHasActiveCompressions,
  setCancelUploadsRequested,
} = slice.actions;

if (!reducer) {
  throw new Error('No reducer found for assets');
}

// eslint-disable-next-line import/no-unused-modules
export {
  reducer,
  slice,
  saveLastLoadedAsset,
  clearLastLoadedAsset,
  setLastAccessedAsset,
  clearLastAccessedAsset,
  setHasPendingPhotoUploads,
  setHasActiveCompressions,
  setCancelUploadsRequested,
};
